<template>
  <div class="player text-left" :class="{'player--dark-mode': darkMode, 'player--ready': playerReady}" ref="main" v-resize="handleResize">
    <div class="player__grid">
      <div class="items-center flex player__grid--top">
        <div class="flex-grow overflow-hidden relative title-wrapper" :class="{'title-wrapper--dark-mode': darkMode, 'title-wrapper--title-animated': titleAnimated}">
          <my-title ref="title"></my-title>
        </div>
        <div class="flex-none flex buttons buttons--top-actions">
          <minimize-button></minimize-button>
          <close-button></close-button>
        </div>
      </div>
      <div class="flex flex-wrap l:flex-no-wrap player__grid--bottom" style="padding-bottom: 21px;">
        <div class="flex-100 l:flex-none">
          <ul class="flex flex-wrap buttons buttons--actions justify-between">
            <li>
              <chapter-previous class></chapter-previous>
            </li>
            <li>
              <step-backward class></step-backward>
            </li>
            <li>
              <play-button class="play-button--fixed"></play-button>
            </li>
            <li>
              <step-forward class></step-forward>
            </li>
            <li>
              <chapter-next class></chapter-next>
            </li>
            <li class="text-xs">
              <speed-counter></speed-counter>
            </li>
          </ul>
        </div>
        <div class="flex-1 items-center flex progress-wrapper">
          <timer-current class="text-xs"></timer-current>
          <progress-bar class="progress-bar--actions"></progress-bar>
          <timer-duration class="text-xs"></timer-duration>
        </div>
      </div>
      <div class="player__grid--right faz-logo" :class="{'faz-logo--dark-mode': darkMode}" />
    </div>
    <div>
      <extended-block>
        <div class="flex tabs">
          <tab-trigger tab="chapters">
            <icon type="chapter"></icon>
          </tab-trigger>
          <tab-trigger tab="playlist" v-if="!isIe()">
            <icon type="playlist"></icon>
          </tab-trigger>
          <tab-trigger tab="subscriptions">
            <icon type="subscriptions"></icon>
          </tab-trigger>
          <tab-trigger tab="shownotes">
            <icon type="subscriptions"></icon>
          </tab-trigger>
        </div>

        <div class="relative overflow-hidden outline-none">
          <tab name="chapters">
            <tab-chapters></tab-chapters>
          </tab>
          <tab name="playlist" v-if="!isIe()">
            <tab-playlist></tab-playlist>
          </tab>
          <tab name="subscriptions" v-if="!isIe()">
            <tab-subscriptions></tab-subscriptions>
          </tab>
          <tab name="shownotes" v-if="!isIe()">
            <tab-shownotes></tab-shownotes>
          </tab>
        </div>
      </extended-block>
    </div>
  </div>
</template>

<script>
import resize from 'vue-resize-directive';
import select from '@/store/selectors';
import { isIe } from '@/utils/detect';
import { PLAYER_RESIZED, PLAYER_MOUNTED } from '@/utils/messages';

import {
  PlayButton,
  CloseButton,
  MinimizeButton,
  MyTitle,
  ExtendedBlock,
  ChapterNext,
  ChapterPrevious,
  StepBackward,
  StepForward,
  TimerDuration,
  TimerCurrent,
  ProgressBar,
  TabTrigger,
  Tab,
  TabChapters,
  TabPlaylist,
  TabSubscriptions,
  TabShownotes,
  SpeedCounter,
} from '@/components';

export default {
  directives: {
    resize,
  },
  components: {
    PlayButton,
    ExtendedBlock,
    CloseButton,
    MinimizeButton,
    MyTitle,
    ChapterNext,
    ChapterPrevious,
    StepBackward,
    StepForward,
    TimerDuration,
    TimerCurrent,
    ProgressBar,
    TabTrigger,
    Tab,
    TabChapters,
    TabPlaylist,
    TabSubscriptions,
    TabShownotes,
    SpeedCounter,
  },
  props: {
    msg: String,
  },
  data() {
    return {
      titleRef: null,

      ...this.mapState({
        background: select.theme.brandDarkest,
        font: select.theme.fontRegular,
        fonts: select.theme.fonts,
        isExtended: select.components.extendedBlock,
        playerId: select.playerId,
        darkMode: select.customOptions.darkMode,
        lifecycle: select.lifecycle,
      }),
    };
  },
  computed: {
    titleAnimated() {
      if (this.titleRef) {
        return this.titleRef.animate;
      }

      return false;
    },

    playerReady() {
      return this.lifecycle === 'ready';
    },
  },
  methods: {
    handleResize(e) {
      window.parent.postMessage(
        {
          type: PLAYER_RESIZED,
          data: {
            playerId: this.playerId,
            height: `${e.offsetHeight}px`,
          },
        },
        '*',
      );
    },
    isIe: () => isIe,
  },
  mounted() {
    this.titleRef = this.$refs.title;

    window.parent.postMessage(
      {
        type: PLAYER_MOUNTED,
        data: {
          playerId: this.playerId,
        },
      },
      '*',
    );
  },
};
</script>

<style scoped lang="scss">
@import "~@/assets/scss/media.scss";

body{
  margin-top: 0;
}

.player {
  border-color: #DDDDDD;
  border-radius: 2px;
  border-width: 1px;
  background-color: #FFFFFF;
  margin: 0 auto;
  padding: 0 15px;
  position: relative;

  &--dark-mode {
    border-color: #454545;
    background-color: #161616;
  }

  &:not(&--ready) {
    display: none;
  }
}

@include only-desktop {
  .player {
    padding: 0 20px;
  }
}

.title-wrapper {
  height: 16px;
  position: relative;

  &::before, &::after {
    content: "";
    position: absolute;
    height: 100%;
    top: 0;
    width: 15px;
    z-index: 1;
  }

  :not(&--title-animated) {
    &::before, &::after {
      display: none;
    }
  }

  &::before {
    left: 0;
    background: linear-gradient(
      to right,
      #FFFFFFFF,
      #FFFFFF00
    );
  }

  &--dark-mode::before {
    background: linear-gradient(
      to right,
      #161616FF,
      #16161600
    );
  }

  &::after {
    right: 0;
    background: linear-gradient(
      to left,
      #FFFFFFFF,
      #FFFFFF00
    );
  }

  &--dark-mode::after {
    background: linear-gradient(
      to left,
      #161616FF,
      #16161600
    );
  }
}

.buttons {
  align-items: center;
  display: flex;
  line-height: 0;
  list-style: none;

  > *:empty {
    display: none;
  }

  &--top-actions {
    > *:not(:last-child) {
      margin-right: 15px;
    }
  }

  &--actions {
    @include only-desktop {
      > * {
        margin-right: 33px;
      }
    }
  }
}

.player__grid {
  display: block;
  grid-template-columns: auto 32px;
  grid-template-rows: auto auto;
  grid-column-gap: 20px;

  &--top {
    height: 49px;
    padding-top: 3px;
    transition: margin-left 0.3s ease-in-out;
    grid-column: 1;
    grid-row: 1;
  }

  &--bottom {
    grid-column: 1;
    grid-row: 2;
  }

  &--right {
    grid-column: 2;
    grid-row: 1 / span 2;
  }
}

.faz-logo {
  display: none;
  background-size: contain;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg fill='%23000000' height='20' width='14' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12.487 1.491c-.31.175-.725.262-1.248.262a5.2 5.2 0 01-1.44-.212A30.74 30.74 0 017.895.907 26.678 26.678 0 005.81.222 6.218 6.218 0 004.17 0c-.887 0-1.646.178-2.277.534-.631.355-1.105.823-1.42 1.4A3.728 3.728 0 000 3.749c0 .779.219 1.407.654 1.884a2.11 2.11 0 001.622.715c.725 0 1.31-.242 1.752-.725l-.08-.141a2.78 2.78 0 01-1.43.403c-.565 0-1.02-.175-1.37-.524-.35-.35-.523-.8-.523-1.35 0-.578.183-1.018.553-1.32.369-.303.877-.454 1.52-.454.484 0 .978.078 1.481.232.504.155 1.125.38 1.863.675.779.323 1.44.564 1.984.726a5.668 5.668 0 001.621.241c.712 0 1.336-.164 1.874-.493.536-.33.956-.759 1.258-1.29.303-.53.486-1.105.554-1.723-.255.417-.537.713-.846.887'/%3E%3Cpath d='M9.89 6.121c-.377 0-.71.108-.998.323-.289.214-.51.486-.664.816a2.28 2.28 0 00-.232.977c-.524-.484-.91-.893-1.158-1.229a1.824 1.824 0 01-.373-1.14c0-.268.077-.553.232-.856.154-.302.345-.58.574-.836-.376.122-.773.333-1.189.635A4.186 4.186 0 005.005 5.98a2.865 2.865 0 00-.453 1.592c0 .604.144 1.115.433 1.532.289.416.708.879 1.259 1.39.51.47.886.873 1.128 1.209.242.335.362.738.362 1.209 0 .39-.1.772-.302 1.148-.86-1.168-1.92-1.797-3.182-1.797-.645 0-1.239.17-1.783.513a3.596 3.596 0 00-1.289 1.44c-.316.619-.473 1.318-.473 2.096 0 .712.164 1.347.494 1.905a3.51 3.51 0 001.349 1.31c.57.314 1.218.473 1.943.473.698 0 1.34-.14 1.924-.423a4.42 4.42 0 001.46-1.108c.39-.457.664-.934.826-1.43l-.202-.101c-.416.765-.954 1.38-1.61 1.843-.66.463-1.411.695-2.257.695-.644 0-1.205-.12-1.681-.362a2.67 2.67 0 01-1.098-.987 2.58 2.58 0 01-.382-1.37c0-.633.203-1.173.614-1.562.41-.39.923-.554 1.54-.554.537 0 1.003.117 1.4.413.396.295.755.728 1.077 1.32.121-.404.315-.715.585-1.044.268-.33.657-.638 1.168-.988.644-.456 1.13-.907 1.46-1.35.329-.443.494-.98.494-1.611 0-.565-.125-1.062-.373-1.492a6.043 6.043 0 00-1.037-1.29s-.02-.113-.02-.24c-.002-.306.07-.6.212-.867a1.28 1.28 0 01.593-.585c-.134.161-.202.376-.202.645 0 .295.098.537.293.726.194.188.44.282.735.282s.557-.119.786-.353c.228-.235.342-.521.342-.857 0-.363-.11-.658-.333-.886-.221-.229-.527-.343-.916-.343'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: 50%;

  &--dark-mode {
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg fill='%23FFFFFF' height='20' width='14' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12.487 1.491c-.31.175-.725.262-1.248.262a5.2 5.2 0 01-1.44-.212A30.74 30.74 0 017.895.907 26.678 26.678 0 005.81.222 6.218 6.218 0 004.17 0c-.887 0-1.646.178-2.277.534-.631.355-1.105.823-1.42 1.4A3.728 3.728 0 000 3.749c0 .779.219 1.407.654 1.884a2.11 2.11 0 001.622.715c.725 0 1.31-.242 1.752-.725l-.08-.141a2.78 2.78 0 01-1.43.403c-.565 0-1.02-.175-1.37-.524-.35-.35-.523-.8-.523-1.35 0-.578.183-1.018.553-1.32.369-.303.877-.454 1.52-.454.484 0 .978.078 1.481.232.504.155 1.125.38 1.863.675.779.323 1.44.564 1.984.726a5.668 5.668 0 001.621.241c.712 0 1.336-.164 1.874-.493.536-.33.956-.759 1.258-1.29.303-.53.486-1.105.554-1.723-.255.417-.537.713-.846.887'/%3E%3Cpath d='M9.89 6.121c-.377 0-.71.108-.998.323-.289.214-.51.486-.664.816a2.28 2.28 0 00-.232.977c-.524-.484-.91-.893-1.158-1.229a1.824 1.824 0 01-.373-1.14c0-.268.077-.553.232-.856.154-.302.345-.58.574-.836-.376.122-.773.333-1.189.635A4.186 4.186 0 005.005 5.98a2.865 2.865 0 00-.453 1.592c0 .604.144 1.115.433 1.532.289.416.708.879 1.259 1.39.51.47.886.873 1.128 1.209.242.335.362.738.362 1.209 0 .39-.1.772-.302 1.148-.86-1.168-1.92-1.797-3.182-1.797-.645 0-1.239.17-1.783.513a3.596 3.596 0 00-1.289 1.44c-.316.619-.473 1.318-.473 2.096 0 .712.164 1.347.494 1.905a3.51 3.51 0 001.349 1.31c.57.314 1.218.473 1.943.473.698 0 1.34-.14 1.924-.423a4.42 4.42 0 001.46-1.108c.39-.457.664-.934.826-1.43l-.202-.101c-.416.765-.954 1.38-1.61 1.843-.66.463-1.411.695-2.257.695-.644 0-1.205-.12-1.681-.362a2.67 2.67 0 01-1.098-.987 2.58 2.58 0 01-.382-1.37c0-.633.203-1.173.614-1.562.41-.39.923-.554 1.54-.554.537 0 1.003.117 1.4.413.396.295.755.728 1.077 1.32.121-.404.315-.715.585-1.044.268-.33.657-.638 1.168-.988.644-.456 1.13-.907 1.46-1.35.329-.443.494-.98.494-1.611 0-.565-.125-1.062-.373-1.492a6.043 6.043 0 00-1.037-1.29s-.02-.113-.02-.24c-.002-.306.07-.6.212-.867a1.28 1.28 0 01.593-.585c-.134.161-.202.376-.202.645 0 .295.098.537.293.726.194.188.44.282.735.282s.557-.119.786-.353c.228-.235.342-.521.342-.857 0-.363-.11-.658-.333-.886-.221-.229-.527-.343-.916-.343'/%3E%3C/svg%3E");
  }
}

@include with-faz-logo {
  .player__grid {
    display: grid;
  }

  .faz-logo {
    display: block;
  }
}

.progress-bar--actions {
  margin: 0 8px;
}

.play-button--fixed {
  width: 21px;
}

@include only-mobile {
  .progress-wrapper {
    margin-top: 15px;
  }
}

.tabs {
  margin: 20px 0 15px;

  @include only-desktop {
    margin-top: 25px;
  }

  > * {
    margin-right: 33px;
  }

  > *:last-child {
    margin-right: 0;
  }
}
</style>
